import {
  useForm,
  useFieldArray,
  useFormContext,
  FormProvider,
  UseFieldArrayReturn,
} from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useState, useMemo } from "react"
import { Loader2, ChevronDown, Check, Plus, Trash2 } from "lucide-react"
import { Button } from "@nasti/ui/button"
import { Input } from "@nasti/ui/input"
import { Label } from "@nasti/ui/label"
import { Textarea } from "@nasti/ui/textarea"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@nasti/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@nasti/ui/popover"
import { useToast } from "@nasti/ui/hooks"
import { cn } from "@nasti/ui/utils"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nasti/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@nasti/ui/alert-dialog"

import {
  useCreateQualityTest,
  useDeleteQualityTest,
  useUpdateQualityTest,
} from "@/hooks/useBatchTests"
import { useSubBatches } from "@/hooks/useSubBatches"
import type { QualityTestType, QualityTest } from "@nasti/common/types"

// Test type options
const testTypeOptions: { value: QualityTestType; label: string }[] = [
  { value: "x-ray", label: "X-Ray" },
  { value: "cut test", label: "Cut Test" },
  { value: "tz", label: "TZ" },
  { value: "germination", label: "Germination" },
]

// Validation schema for a single replicate
const replicateSchema = z.object({
  weight_grams: z.coerce.number().min(0.001, "Weight must be greater than 0"),
  viable_seed_count: z.coerce
    .number()
    .int("Must be a whole number")
    .min(0, "Count cannot be negative"),
  dead_seed_count: z.coerce
    .number()
    .int("Must be a whole number")
    .min(0, "Count cannot be negative"),
})

// Main form schema
const qualityTestSchema = z.object({
  test_type: z.enum(["x-ray", "cut test", "tz", "germination"]),
  psu_grams: z.coerce.number().min(0.001, "PSU must be greater than 0"),
  inert_seed_weight_grams: z.coerce.number().default(0),
  other_species_seeds_grams: z.coerce.number().optional().default(0),
  relative_humidity_percent: z.coerce
    .number()
    .min(0, "RH must be at least 0%")
    .max(100, "RH cannot exceed 100%")
    .optional(),
  repeats: z
    .array(replicateSchema)
    .min(1, "At least one replicate is required")
    .max(5, "Maximum 5 replicates allowed"),
  notes: z.string().optional(),
})

type QualityTestFormData = z.infer<typeof qualityTestSchema>

// `weightLocked`: the seed a recorded test consumed is fixed, so its replicate
// weights, and how many replicates there are, cannot change on edit.
const SeedCounts = ({
  fieldArray,
  weightLocked,
}: {
  fieldArray: UseFieldArrayReturn<QualityTestFormData>
  weightLocked: boolean
}) => {
  const { register, formState, watch } = useFormContext<QualityTestFormData>()
  const { fields, remove } = fieldArray

  const getDeadSeedCount = (index: number) =>
    parseInt(watch(`repeats.${index}.dead_seed_count`) as unknown as string) ||
    0
  const getViableSeedCount = (index: number) =>
    parseInt(
      watch(`repeats.${index}.viable_seed_count`) as unknown as string,
    ) || 0
  const getTotalSeedCount = (index: number) =>
    getDeadSeedCount(index) + getViableSeedCount(index)

  return fields.map((field, index) => (
    <div key={field.id} className="rounded-lg border p-2">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-sm font-medium">Replicate {index + 1}</span>
        {fields.length > 1 && !weightLocked && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => remove(index)}
            className="cursor-pointer text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {/* Weight */}
        <div className="space-y-1">
          <Label htmlFor={`repeats.${index}.weight_grams`}>Weight (g) *</Label>
          <Input
            type="number"
            step="0.001"
            min="0"
            placeholder="0.000"
            readOnly={weightLocked}
            {...register(`repeats.${index}.weight_grams`)}
          />
          {formState.errors.repeats?.[index]?.weight_grams && (
            <p className="text-xs text-red-600">
              {formState.errors.repeats[index]?.weight_grams?.message}
            </p>
          )}
        </div>

        {/* Viable Seeds */}
        <div className="space-y-1">
          <Label htmlFor={`repeats.${index}.viable_seed_count`}>
            Viable Seeds *
          </Label>
          <Input
            type="number"
            min="0"
            step="1"
            placeholder="0"
            {...register(`repeats.${index}.viable_seed_count`)}
          />
          {formState.errors.repeats?.[index]?.viable_seed_count && (
            <p className="text-xs text-red-600">
              {formState.errors.repeats[index]?.viable_seed_count?.message}
            </p>
          )}
        </div>

        {/* Dead Seeds */}
        <div className="space-y-1">
          <Label htmlFor={`repeats.${index}.dead_seed_count`}>
            Dead Seeds *
          </Label>
          <Input
            type="number"
            min="0"
            step="1"
            placeholder="0"
            {...register(`repeats.${index}.dead_seed_count`)}
          />
          {formState.errors.repeats?.[index]?.dead_seed_count && (
            <p className="text-xs text-red-600">
              {formState.errors.repeats[index]?.dead_seed_count?.message}
            </p>
          )}
        </div>

        {/* Seed Count */}
        <div className="space-y-1">
          <Label>Seed Count (calculated)</Label>
          <Input
            type="number"
            min="0"
            step="1"
            disabled
            placeholder="0"
            value={getTotalSeedCount(index)}
          />
          {formState.errors.repeats?.[index]?.dead_seed_count && (
            <p className="text-xs text-red-600">
              {formState.errors.repeats[index]?.dead_seed_count?.message}
            </p>
          )}
        </div>
      </div>
    </div>
  ))
}

type QualityTestFormProps = {
  batchId: string
  subBatchId?: string
  existingTest?: QualityTest
  onSuccess?: () => void
  onCancel?: () => void
  className?: string
}

export const QualityTestForm = ({
  batchId,
  subBatchId,
  existingTest,
  onSuccess,
  onCancel,
  className,
}: QualityTestFormProps) => {
  const { toast } = useToast()
  const { mutateAsync: createTest, isPending: isCreating } =
    useCreateQualityTest()
  const { mutateAsync: updateTest, isPending: isUpdating } =
    useUpdateQualityTest()
  const { mutateAsync: deleteTest, isPending: isDeleting } =
    useDeleteQualityTest()
  const [testTypeOpen, setTestTypeOpen] = useState(false)

  const { data: subBatches, isLoading: subBatchesLoading } =
    useSubBatches(batchId)

  const [selectedSubBatchId, setSelectedSubBatchId] = useState<string | null>(
    subBatchId ?? null,
  )

  // Auto-select if only one sub-batch
  const effectiveSubBatchId = useMemo(() => {
    if (selectedSubBatchId) return selectedSubBatchId
    if (subBatches?.length === 1) return subBatches[0].id
    return null
  }, [selectedSubBatchId, subBatches])

  const isPending = isCreating || isUpdating || isDeleting
  const isEditing = Boolean(existingTest)

  // Form setup with default values
  const form = useForm<QualityTestFormData>({
    resolver: zodResolver(qualityTestSchema),
    defaultValues: existingTest?.result || {
      test_type: "x-ray",
      inert_seed_weight_grams: 0,
      other_species_seeds_grams: 0,
      repeats: [
        {
          weight_grams: 0,
          viable_seed_count: 0,
          dead_seed_count: 0,
        },
      ],
      notes: "",
    },
  })

  const { register, control } = form

  // Field array for repeats
  const fieldArray = useFieldArray({
    control,
    name: "repeats",
  })

  const onSubmit = async (data: QualityTestFormData) => {
    if (!isEditing && !effectiveSubBatchId) {
      toast({
        description: "Please select a bag",
        variant: "destructive",
      })
      return
    }

    try {
      if (isEditing && existingTest) {
        await updateTest({
          id: existingTest.id,
          result: data,
        })

        toast({
          description: "Quality test updated successfully",
        })
      } else {
        await createTest({
          batchId,
          subBatchId: effectiveSubBatchId!,
          result: data,
        })

        toast({
          description: "Quality test recorded successfully",
        })
      }

      onSuccess?.()
    } catch (error) {
      console.error("Failed to save quality test:", error)
      toast({
        description: `Failed to ${isEditing ? "update" : "record"} quality test`,
        variant: "destructive",
      })
    }
  }

  const onDelete = async () => {
    if (!existingTest) return

    try {
      await deleteTest(existingTest)
      toast({ description: "Quality test deleted" })
      onSuccess?.()
    } catch (error) {
      console.error("Failed to delete quality test:", error)
      toast({
        description:
          error instanceof Error
            ? error.message
            : "Failed to delete quality test",
        variant: "destructive",
      })
    }
  }

  const addReplicate = () => {
    if (fieldArray.fields.length < 5) {
      fieldArray.append({
        weight_grams: 0,
        viable_seed_count: 0,
        dead_seed_count: 0,
      })
    }
  }

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn("space-y-4", className)}
      >
        {/* Sub-batch selection (only for new tests, when multiple sub-batches exist) */}
        {!isEditing && (
          <div className="space-y-2">
            <Label>Bag (seed source) *</Label>
            {subBatchesLoading ? (
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading bags...
              </div>
            ) : subBatches && subBatches.length > 1 ? (
              <Select
                value={effectiveSubBatchId ?? undefined}
                onValueChange={setSelectedSubBatchId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select bag" />
                </SelectTrigger>
                <SelectContent>
                  {subBatches.map((sb, index) => (
                    <SelectItem key={sb.id} value={sb.id}>
                      Bag {index + 1} ({sb.current_weight}g)
                      {sb.notes ? ` — ${sb.notes}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : subBatches?.length === 1 ? (
              <p className="text-muted-foreground text-sm">
                {subBatches[0].weight_grams}g
                {subBatches[0].notes ? ` — ${subBatches[0].notes}` : ""}
              </p>
            ) : (
              <p className="text-sm text-red-600">
                No bags found for this batch
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          {/* Test Type */}
          <div className="space-y-2">
            <Label htmlFor="test_type">Test Type *</Label>
            <Popover open={testTypeOpen} onOpenChange={setTestTypeOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between"
                  disabled={isEditing}
                >
                  {testTypeOptions.find(
                    (t) => t.value === form.watch("test_type"),
                  )?.label || "Select test type"}
                  <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandList>
                    <CommandEmpty>No test type found.</CommandEmpty>
                    <CommandGroup>
                      {testTypeOptions.map((testType) => (
                        <CommandItem
                          key={testType.value}
                          value={testType.value}
                          onSelect={() => {
                            form.setValue("test_type", testType.value)
                            setTestTypeOpen(false)
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              form.watch("test_type") === testType.value
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          {testType.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {form.formState.errors.test_type && (
              <p className="text-sm text-red-600">
                {form.formState.errors.test_type.message}
              </p>
            )}
          </div>
          {/* Relative Humidity */}
          <div className="space-y-2">
            <Label htmlFor="relative_humidity_percent">
              Relative Humidity (%)
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="relative_humidity_percent"
                type="number"
                step="0.1"
                min="0"
                max="100"
                placeholder="Enter RH percentage"
                {...register("relative_humidity_percent")}
                className="flex-1"
              />
              <span className="text-muted-foreground min-w-[20px] text-sm">
                %
              </span>
            </div>
            {form.formState.errors.relative_humidity_percent && (
              <p className="text-sm text-red-600">
                {form.formState.errors.relative_humidity_percent.message}
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {/* PSU */}
          <div className="space-y-2">
            <Label htmlFor="psu_grams">PS (grams) *</Label>
            <div className="flex items-center gap-2">
              <Input
                id="psu_grams"
                type="number"
                step="0.001"
                min="0"
                placeholder="Enter PSU weight"
                {...register("psu_grams")}
                className="flex-1"
              />
              <span className="text-muted-foreground min-w-[20px] text-sm">
                g
              </span>
            </div>
            {form.formState.errors.psu_grams && (
              <p className="text-sm text-red-600">
                {form.formState.errors.psu_grams.message}
              </p>
            )}
          </div>

          {/* Inert Seed Weight (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="inert_seed_weight_grams">
              Inert Seed Weight (grams) *
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="inert_seed_weight_grams"
                type="number"
                step="0.001"
                min="0"
                {...register("inert_seed_weight_grams")}
                className="flex-1"
              />
              <span className="text-muted-foreground min-w-[20px] text-sm">
                g
              </span>
            </div>
            {form.formState.errors.inert_seed_weight_grams && (
              <p className="text-sm text-red-600">
                {form.formState.errors.inert_seed_weight_grams.message}
              </p>
            )}
          </div>

          {/* Other Species Seeds (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="other_species_seeds_grams">
              Other Species Seeds (grams)
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="other_species_seeds_grams"
                type="number"
                step="0.001"
                min="0"
                {...register("other_species_seeds_grams")}
                className="flex-1"
              />
              <span className="text-muted-foreground min-w-[20px] text-sm">
                g
              </span>
            </div>
            {form.formState.errors.other_species_seeds_grams && (
              <p className="text-sm text-red-600">
                {form.formState.errors.other_species_seeds_grams.message}
              </p>
            )}
          </div>
        </div>

        {/* Repeats Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Seed Counts (1-5 replicates) *</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addReplicate}
              disabled={isEditing || fieldArray.fields.length >= 5}
              className="cursor-pointer"
            >
              <Plus className="mr-1 h-4 w-4" />
              Add Replicate
            </Button>
          </div>

          <SeedCounts fieldArray={fieldArray} weightLocked={isEditing} />
          {isEditing && (
            <p className="text-muted-foreground text-xs">
              The test type and replicate weights record the seed this test
              used, so they cannot be changed. If they are wrong, delete the
              test and record it again.
            </p>
          )}

          {form.formState.errors.repeats && (
            <p className="text-sm text-red-600">
              {Array.isArray(form.formState.errors.repeats)
                ? "Please fix errors in replicates"
                : form.formState.errors.repeats.message}
            </p>
          )}
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            placeholder="Additional test notes..."
            {...register("notes")}
            rows={3}
          />
          {form.formState.errors.notes && (
            <p className="text-sm text-red-600">
              {form.formState.errors.notes.message}
            </p>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4">
          {isEditing && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isPending}
                  className="mr-auto cursor-pointer text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <Trash2 className="mr-1 h-4 w-4" />
                  Delete Test
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Quality Test</AlertDialogTitle>
                  <AlertDialogDescription>
                    The test will be removed and any seed it used will be added
                    back to its bag. Record it again if it was entered wrongly.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {onCancel && (
            <Button
              type="button"
              className="cursor-pointer"
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            disabled={isPending || (!isEditing && !effectiveSubBatchId)}
            className="min-w-[120px] cursor-pointer"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? "Update Test" : "Record Test"}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
